/**
 * @file        src/components/RichTextEditor.jsx
 * @description Lightweight contentEditable rich-text editor (no dependency).
 *              Toolbar: bold/italic/underline, H2/H3, lists, link, clear.
 *              Emits HTML via onChange. Good enough for blog bodies.
 * @author      Kinetika Labs
 * @created     2026-07-01
 * @changelog
 *   - 2026-07-01 Claude — Initial (built-in editor).
 */
import { useEffect, useRef } from 'react';
import Icon from './Icon';
import { promptText } from '../lib/confirm';

const TOOLS = [
  { cmd: 'bold', label: 'B', style: { fontWeight: 800 } },
  { cmd: 'italic', label: 'I', style: { fontStyle: 'italic' } },
  { cmd: 'underline', label: 'U', style: { textDecoration: 'underline' } },
  { cmd: 'formatBlock', arg: 'H2', label: 'H2' },
  { cmd: 'formatBlock', arg: 'H3', label: 'H3' },
  { cmd: 'formatBlock', arg: 'P', label: '¶' },
  { cmd: 'insertUnorderedList', label: '• List' },
  { cmd: 'insertOrderedList', label: '1. List' },
];

export default function RichTextEditor({ value = '', onChange }) {
  const ref = useRef(null);
  const focused = useRef(false);

  // Sync external value in only when the user isn't actively typing.
  useEffect(() => {
    if (ref.current && !focused.current && ref.current.innerHTML !== (value || '')) {
      ref.current.innerHTML = value || '';
    }
  }, [value]);

  function emit() {
    onChange(ref.current?.innerHTML || '');
  }

  function exec(cmd, arg) {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    emit();
  }

  async function addLink() {
    const url = await promptText('Link URL (https://…)');
    if (url) exec('createLink', url);
  }

  return (
    <div className="rte">
      <div className="rte-toolbar">
        {TOOLS.map((t) => (
          <button key={t.label} type="button" className="rte-btn" style={t.style}
                  title={t.label} onMouseDown={(e) => { e.preventDefault(); exec(t.cmd, t.arg); }}>
            {t.label}
          </button>
        ))}
        <button type="button" className="rte-btn" title="Add link"
                onMouseDown={(e) => { e.preventDefault(); addLink(); }}>
          <Icon name="page" size={14} /> Link
        </button>
        <button type="button" className="rte-btn" title="Clear formatting"
                onMouseDown={(e) => { e.preventDefault(); exec('removeFormat'); }}>
          <Icon name="x" size={14} /> Clear
        </button>
      </div>
      <div
        ref={ref}
        className="rte-body"
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onFocus={() => { focused.current = true; }}
        onBlur={() => { focused.current = false; emit(); }}
      />
    </div>
  );
}
